/* eslint-disable import/prefer-default-export */

import { replaceDecimalCode } from 'oxygen-lib-ui/util/string';
import { getIsoDate } from 'oxygen-lib-ui/helpers/date';
import { parse as parseJson } from 'util/json';
import { removeKeysWithUndefinedValues } from 'util/objects';
import {
  updateImageUrlsInJsonLd,
  fixJsonLdDateValues
} from 'components/Metadata/helpers';
import { SITE_NAME, SITE_ORIGIN, PUBLISHER_LOGO } from 'constants/site';
import { TWITTER_HANDLE } from 'constants/vendor';
import { decodeEntities } from 'util/html';

export const JSON_LD_MIME_TYPE = 'application/ld+json';

// which variants we want to extract data from. see
// components/Metadata/index.js for a complete list of variants
export const METADATA_VARIANTS = ['default', 'links', 'scripts', 'hidden'];

// list of fields that could possibly have HTML entities in them
export const HTML_FIELDS = ['title'];

/**
 * accepts name and value, and returns an object with mapped name/value
 *
 * @export
 * @param {Object} item metadata item
 * @param {String} item.name
 * @param {String} item.value
 * @return {Object} object with mapped name/value
 */
export function processMetadataItem({ name, value }) {
  // conditionally decode HTML entities based on name. we need to decode the
  // entities because the consumers of this metadata (analytics tools, etc.) do
  // NOT expect HTML entities in values.
  if (HTML_FIELDS.includes(name)) {
    return { [name]: replaceDecimalCode(value) };
  }

  // move the stringified JSON-LD data to a different key to signify that we've
  // parsed it.
  //
  // CLEANUP: we really should be assembling the JSON-LD on our side, not
  // receiving it serialized.
  if (name === JSON_LD_MIME_TYPE) {
    return { jsonLd: parseJson(value, {}) };
  }

  return { [name]: value };
}

/**
 * extracts/processes the metadata bundle items and extracts all necessary
 * metadata from them for use by analytics (and possibly other things).
 *
 * this basically aggregates the data name/value pairs across the
 * METADATA_VARIANTS and creates a more compact object out of them.
 *
 * CLEANUP: resolve how this does or does not overlap with the Metadata
 * component.
 *
 * @export
 * @param {Object[]} bundleItems array of items from metadata bundle
 * @param {String} bundleItems[].variant
 * @param {Object} bundleItems[].data
 * @param {String} bundleItems[].data.name
 * @param {String} bundleItems[].data.value
 * @returns {Object} metadata extracted from bundle items
 */
export function extractMetadataFromBundleItems(bundleItems = []) {
  const metadata = bundleItems.reduce(
    // CLEANUP: this is really dumb and will change Real Soon once we establish
    // exact metadata we need and where it comes from.
    (acc, curr) =>
      METADATA_VARIANTS.includes(curr.variant)
        ? {
            ...acc,
            ...processMetadataItem({
              name: curr.data.name,
              value:
                typeof curr.data.value === 'string'
                  ? decodeEntities(curr.data.value)
                  : curr.data.value
            })
          }
        : acc,
    {}
  );

  return metadata;
}

/**
 * creates a metadata object that matches the shape of the object produced by
 * extractMetadataFromBundleItems.
 *
 * useful where we lack a metadata bundle, like when we aren't getting a page's
 * data from page bundler.
 *
 * if any metadata keys would end up with undefined values, they will not be
 * returned.
 *
 * @param {Object} data
 * @param {String} data.title page title
 * @param {String} data.description page description
 * @param {String} data.thumbnail thumbnail image URL
 * @param {String} data.robots robots meta value
 * @param {String} data.canonical page's canonical URL
 * @param {String} data.author author name
 * @param {String} data.section section name (used for qc:section and JSON-LD sectionName)
 * @param {Number} data.publishedAt publish datetime in unix epoch format
 * @param {Number} data.modifiedAt modification datetime in unix epoch format
 * @param {Number} data.updatedAt update/refresh datetime in unix epoch format
 * @param {Number} data.repromotedAt repromotion datetime in unix epoch format
 * @param {String} data.ogType opengraph type
 * @param {String} data.socialTitle title to use for opengraph and twitter
 * @param {String} data.socialDescription description to use for opengraph and twitter
 * @param {String} data.socialImage image URL to use for opengraph and twitter
 * @param {String} data.flipboardTitle title to use for flipboard
 * @param {String} data.flipboardDescription description to use for flipboard
 * @param {String} data.flipboardImage image URL to use for flipboard
 * @param {String} data.jsonLdType JSON-LD schema type
 * @param {String[]} data.categories categories, used for keywords in JSON-LD
 * @param {String[]} data.prismColors colors in prism page
 * @param {String[]} data.prismTags tags in prism page
 */
export function createMetadataObject(data = {}) {
  const {
    title,
    description,
    thumbnail,
    robots,
    canonical,
    author,
    section,
    publishedAt,
    modifiedAt,
    updatedAt,
    repromotedAt,
    ogType,
    ogTitle,
    ogDescription,
    ogImage,
    socialTitle,
    socialDescription,
    socialImage,
    flipboardTitle,
    flipboardDescription,
    flipboardImage,
    jsonLdType,
    categories,
    prismColors,
    prismTags
  } = data;

  const rssUrl = `${SITE_ORIGIN}/main.rss`;

  // CLEANUP: might be to factor JSON-LD creation out to another method
  const jsonLd = removeKeysWithUndefinedValues(
    fixJsonLdDateValues(
      updateImageUrlsInJsonLd({
        '@context': 'https://schema.org',
        '@type': jsonLdType || 'WebPage',
        status: 'published',
        url: canonical,
        headline: title,

        thumbnailUrl: thumbnail,
        ...(thumbnail && {
          image: {
            '@type': 'ImageObject',
            url: thumbnail
          }
        }),

        ...(author && {
          creator: {
            '@type': 'Person',
            name: author
          },
          author: {
            '@type': 'Person',
            name: author
          }
        }),

        dateCreated: publishedAt && getIsoDate(publishedAt),
        datePublished: publishedAt && getIsoDate(publishedAt),
        dateModified: modifiedAt && getIsoDate(modifiedAt),

        // including dateUpdated and dateRepromoted for consistency/sanity with
        // the logic in <Metadata />. note this entire object is wrapped in
        // fixJsonLdDateValues().
        dateUpdated: updatedAt && getIsoDate(updatedAt),
        dateRepromoted: repromotedAt && getIsoDate(repromotedAt),

        articleSection: section,
        keywords: categories,
        publisher: {
          '@type': 'Organization',
          logo: {
            '@type': 'ImageObject',
            url: PUBLISHER_LOGO,
            width: 180,
            height: 46
          },
          name: 'Apartment Therapy, LLC.'
        }
      })
    )
  );

  const metadata = {
    title,
    description,
    thumbnail,
    robots,
    canonical,
    alternate: rssUrl,

    // CLEANUP: devise mapping
    'qc:section': section,

    'og:title': ogTitle || socialTitle || title,
    'og:description': ogDescription || socialDescription || description,
    'og:image': ogImage || socialImage || thumbnail,
    'og:url': canonical,
    'og:type': ogType || 'website',
    'og:site_name': SITE_NAME,

    'twitter:card': 'summary_large_image',
    'twitter:title': socialTitle || title,
    'twitter:description': socialDescription || description,
    'twitter:image': socialImage || thumbnail,
    'twitter:url': canonical,
    'twitter:site': TWITTER_HANDLE,

    'flipboard-title': flipboardTitle || title,
    'flipboard-article': flipboardDescription || description,
    'flipboard-image': flipboardImage || thumbnail,
    'flipboard-author': author,
    'flipboard-date': publishedAt,

    'application/ld+json': JSON.stringify(jsonLd),

    prismColors,
    prismTags
  };

  const cleanedMetadata = removeKeysWithUndefinedValues(metadata);

  return cleanedMetadata;
}
